import {ModelFetchOptions} from 'backbone';

import {ArchiveRecord} from 'archive/ArchiveConstants';
import {getArchivePurpose} from 'archive/ArchivePurposesUtil';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import {NumberIDFolder, StringIDFolder} from 'constants/FolderConstants';
import {BioentityAliasInfo} from 'constants/RegistryConstants';
import {User} from 'constants/UserConstants';
import BaseFolderItem, {BaseFolderItemAttributes} from 'folder-items/BaseFolderItem';
import {ThirdPartyStorageServiceData} from 'folder-items/FileConstants';
import normalizeFolder from 'folders/util/normalizeFolder';
import {doQuerySafe, writeQuery} from 'graphql-client/ApolloWrappers';
import {
  graphQLArrayToREST,
  graphQLToREST,
  restArrayToGraphQL,
  restToGraphQL,
} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import initializeWatchQuery from 'graphql-client/initializeWatchQuery';
import QueryWrapper from 'graphql-client/QueryWrapper';
import {toNumberIDFolder, withNonNullSource} from 'nav/graphql/folder/Folder';
import ensureMutable from 'notebook/store/ensureMutable';
import {FromServerNote} from 'notebook/util/NoteUtilConstants';
import {SchemaValidationState} from 'schemas/SchemaValidationConstants';
import {asJQueryPromise} from 'util/Promises';
import {Optional} from 'util/Types';

import {ArchiveRecordGraphQL} from '../../graphql-fragments/app-core/platform/archivable/__generated/ArchiveRecordGraphQL';
import {InaccessibleInfoGraphQL} from '../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {BaseFolderItemFileGraphQL} from '../../graphql-fragments/nav/folder-items/__generated/BaseFolderItemFileGraphQL';
import {AuthorGraphQL} from '../../graphql-fragments/parties/__generated/AuthorGraphQL';
import {TagSchemaGraphQL} from '../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaGraphQL';

export type FolderItemOrInaccessibleGraphQL = BaseFolderItemFileGraphQL;
export type AccessibleFolderItemGraphQL = Exclude<FolderItemOrInaccessibleGraphQL, InaccessibleInfoGraphQL>;

interface BioentityAliasInfoGraphQL {
  id: string;
  alias: string;
  registryCollectionOwnerJson: BioentityAliasInfo['registryCollectionOwner'];
}

export interface FolderItemFileQueryVariables {
  fileId: string;
}

function getPresentProperty<T extends unknown>(obj: AccessibleFolderItemGraphQL, key: string): T | undefined {
  return obj.hasOwnProperty(key) ? (obj as unknown as Record<string, T>)[key] : undefined;
}

function getFolder(folderItem: AccessibleFolderItemGraphQL): StringIDFolder {
  return normalizeFolder(graphQLToREST('FolderSummary', folderItem.folder));
}

function mapBioentityAliasInfos(
  bioentityAliasInfos: Array<BioentityAliasInfoGraphQL>
): Array<BioentityAliasInfo> {
  return bioentityAliasInfos.map(({alias, registryCollectionOwnerJson}) => ({
    alias,
    registryCollectionOwner: registryCollectionOwnerJson,
  }));
}

interface ApolloFolderItemBaseAttributes<
  TFolderItemGraphQL extends AccessibleFolderItemGraphQL,
> extends BaseFolderItemAttributes {
  unsubscribeApollo: () => void;
  folderItem: TFolderItemGraphQL;
}

interface ApolloFolderItemBase<
  TQuery,
  TFolderItemGraphQL extends AccessibleFolderItemGraphQL,
  TFolderItemQueryVariables extends FolderItemFileQueryVariables = FolderItemFileQueryVariables,
> {
  get<K extends keyof ApolloFolderItemBaseAttributes<TFolderItemGraphQL>>(
    key: K
  ): ApolloFolderItemBaseAttributes<TFolderItemGraphQL>[K];
  set<K extends keyof ApolloFolderItemBaseAttributes<TFolderItemGraphQL>>(
    attributeName: K,
    value: ApolloFolderItemBaseAttributes<TFolderItemGraphQL>[K],
    options?: Backbone.ModelSetOptions
  ): ApolloFolderItemBase<TQuery, TFolderItemGraphQL, TFolderItemQueryVariables>;
  set(
    obj: Partial<ApolloFolderItemBaseAttributes<TFolderItemGraphQL>>
  ): ApolloFolderItemBase<TQuery, TFolderItemGraphQL, TFolderItemQueryVariables>;
}

abstract class ApolloFolderItemBase<
  TQuery,
  TFolderItemGraphQL extends AccessibleFolderItemGraphQL,
  TFolderItemQueryVariables extends FolderItemFileQueryVariables = FolderItemFileQueryVariables,
> extends BaseFolderItem {
  override initialize(...args: Array<unknown>): void {
    super.initialize(...args);
    const querySubscription = initializeWatchQuery<TQuery>(
      {...this.getQueryWrapper(), variables: this.queryVariables()},
      this.onChange.bind(this)
    );
    this.set({
      unsubscribeApollo: () => {
        querySubscription.unsubscribe();
      },
    });
  }

  cleanUp(): void {
    this.get('unsubscribeApollo')();
  }

  private doQuery(): Promise<TFolderItemGraphQL> {
    return new Promise((resolve, reject) => {
      doQuerySafe<TQuery, TFolderItemQueryVariables>(this.getQueryWrapper(), {
        variables: this.queryVariables(),
        // We want to re-fetch the latest folder item data from the network every time a workspace tab is
        // opened, rather than showing potentially-outdated data from the cache (which could happen if the tab
        // had previously been opened during the same page load)
        fetchPolicy: 'network-only',
      }).then((data) => {
        const folderItem = this.getGraphQLFolderItemFromQueryData(data);
        if (isInaccessible(folderItem)) {
          reject(folderItem);
        } else {
          resolve(folderItem);
        }
      });
    });
  }

  override fetch(options: ModelFetchOptions): JQueryPromise<TFolderItemGraphQL> {
    return asJQueryPromise(this.doQuery());
  }

  protected abstract getQueryWrapper(): QueryWrapper<TQuery, TFolderItemQueryVariables>;

  protected abstract queryVariables(): TFolderItemQueryVariables;

  protected abstract getGraphQLFolderItemFromQueryData(
    data: TQuery
  ): TFolderItemGraphQL | InaccessibleInfoGraphQL;

  onChange(data: TQuery, loading: boolean): void {
    if (loading) {
      return;
    }

    const folderItem = this.getGraphQLFolderItemFromQueryData(data);
    if (isInaccessible(folderItem)) {
      return;
    }

    const maybeTagSchema = getPresentProperty<TagSchemaGraphQL>(folderItem, 'tagSchema');
    const tagSchema = maybeTagSchema != null ? graphQLToREST('TagSchema', maybeTagSchema) : null;

    this.set({
      folderItem,
      tagSchema,
      name: folderItem.name,
      editURL: folderItem.editUrl,
      folder: getFolder(folderItem),
    });
  }

  protected folderItem(): TFolderItemGraphQL {
    return this.get('folderItem');
  }

  override name(): string {
    return this.folderItem().name;
  }

  override displayLabel(): Optional<string> {
    return getPresentProperty<string>(this.folderItem(), 'displayLabel');
  }

  override folder(): StringIDFolder {
    return getFolder(this.folderItem());
  }

  override projectFolderId(): Optional<string> {
    const projectFolder = getPresentProperty<{id: string}>(this.folderItem(), 'projectFolder');
    return projectFolder != null ? projectFolder.id : null;
  }

  override editURL(): string {
    return this.folderItem().editUrl;
  }

  override initialNote(): Optional<FromServerNote> {
    // The Apollo Cache will return items that are marked to prevent extension but our NoteStores rely on
    // being able to mutate the objects. So we call `ensureMutable` to make sure that we have an object that is
    // extensible and can be mutated.
    return ensureMutable(getPresentProperty<FromServerNote>(this.folderItem(), 'noteJson'));
  }

  override permissions(): Permissions {
    const {__typename, ...permissions} = this.folderItem().permissions;
    return permissions as Permissions;
  }

  override modifiedAt(): string {
    return this.folderItem().modifiedAt;
  }

  override createdAt(): string {
    return this.folderItem().createdAt;
  }

  override archiveRecord(): ArchiveRecord | null {
    const folderItem = this.folderItem();
    return isArchived(folderItem) ? ({purpose: getArchivePurpose(folderItem)} as ArchiveRecord) : null;
  }

  override authors(): Array<User> {
    const authors = getPresentProperty<Array<AuthorGraphQL>>(this.folderItem(), 'authors');
    return authors != null ? (graphQLArrayToREST('Author', authors) as Array<User>) : [];
  }

  override setAuthors(authors: Array<User>): void {
    // @ts-expect-error: `BulkAssembly` does not support authors, so this line generically fails type checks.
    // However, we only expect the folder item types that support authors to call `setAuthors`. Ideally we
    // could assert that this method is only called if `TFolderItemGraphQL` contains `authors`, and remove
    // this TypeScript suppression.
    this.updateFolderItemAttributeInApolloCache('authors', restArrayToGraphQL('Author', authors));
  }

  override initialBioentityAliasInfos(): Optional<Array<BioentityAliasInfo>> {
    const bioentityAliasInfos = getPresentProperty<Array<BioentityAliasInfoGraphQL>>(
      this.folderItem(),
      'bioentityAliasInfos'
    );
    return bioentityAliasInfos != null ? mapBioentityAliasInfos(bioentityAliasInfos) : null;
  }

  override thirdPartyStorageServiceData(): ThirdPartyStorageServiceData {
    return this.folderItem().thirdPartyStorageServiceDataJson;
  }

  override schemaValidationState(): Optional<SchemaValidationState> {
    return getPresentProperty<SchemaValidationState>(this.folderItem(), 'schemaValidationStateJson');
  }

  protected updateFolderItemAttributeInApolloCache<K extends keyof TFolderItemGraphQL>(
    key: K,
    value: TFolderItemGraphQL[K]
  ): void {
    writeQuery(this.getQueryWrapper(), this.getUpdatedQueryData({[key]: value}), this.queryVariables());
  }

  protected abstract getUpdatedQueryData(attrs: Partial<AccessibleFolderItemGraphQL>): TQuery;

  protected batchUpdateFolderItemAttributesInApolloCache(attrs: Partial<AccessibleFolderItemGraphQL>): void {
    writeQuery(this.getQueryWrapper(), this.getUpdatedQueryData(attrs), this.queryVariables());
  }

  override setFolder(folder: StringIDFolder | NumberIDFolder): void {
    this.updateFolderItemAttributeInApolloCache(
      'folder',
      restToGraphQL('FolderSummary', withNonNullSource(toNumberIDFolder(folder)))
    );
  }

  override clearProjectFolder(): void {
    // No-op here because only registry items have this attribute; handled in ApolloRegistryItemBase
  }

  override setProjectFolder(folder: StringIDFolder | NumberIDFolder): void {
    // No-op here because only registry items have this attribute; handled in ApolloRegistryItemBase
  }

  override setPermissions(permissions: Permissions): void {
    this.updateFolderItemAttributeInApolloCache('permissions', {
      __typename: 'PermissionsNode',
      ...permissions,
    } as TFolderItemGraphQL['permissions']);
  }

  private setArchiveRecordForCoreObjectArchivable(archiveRecord: ArchiveRecordGraphQL | null): void {
    const archiveDataKey = 'archiveData' as keyof TFolderItemGraphQL;
    const archiveDataValue =
      archiveRecord != null
        ? {__typename: 'ArchiveDataNode', archived: true, purpose: archiveRecord.purpose}
        : {__typename: 'ArchiveDataNode', archived: false, purpose: null};
    this.updateFolderItemAttributeInApolloCache(
      archiveDataKey,
      archiveDataValue as TFolderItemGraphQL[typeof archiveDataKey]
    );
  }

  private setArchiveRecordForHasArchiveRecord(archiveRecord: ArchiveRecordGraphQL | null): void {
    const archiveRecordKey = 'archiveRecord' as keyof TFolderItemGraphQL;
    this.updateFolderItemAttributeInApolloCache(
      archiveRecordKey,
      archiveRecord as TFolderItemGraphQL[typeof archiveRecordKey]
    );
  }

  override setArchiveRecord(archiveRecord: ArchiveRecordGraphQL | null): void {
    const folderItem = this.folderItem();
    if (folderItem.hasOwnProperty('archiveData')) {
      this.setArchiveRecordForCoreObjectArchivable(archiveRecord);
    } else if (folderItem.hasOwnProperty('archiveRecord')) {
      this.setArchiveRecordForHasArchiveRecord(archiveRecord);
    } else {
      assert(
        false,
        'archiveRecord called but neither ArchivableInterface nor HasArchiveRecordInterface is implemented'
      );
    }
  }

  override setSchemaValidationState(schemaValidationState: Optional<SchemaValidationState>): void {
    // No-op here because only registry items have this attribute; handled in ApolloRegistryItemBase
  }

  override setNameAndDisplayLabel(name: string, displayLabel: string): void {
    this.updateFolderItemAttributeInApolloCache('name', name);
    // Only registry items have displayLabel; this is handled in ApolloRegistryItemBase
  }
}

export default ApolloFolderItemBase;
